/*
**  rui-inline-notification.js
**
**  rui-inline-notification component
*/
import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
export class RuiInlineNotification extends LitElement {
    static get is() {
        return 'rui-inline-notification';
    }
    static get properties() {
        return {
            type: { type: String },
            iconType: { type: String },
            isClosable: { type: Object },
            isNotificationDisplayed: { type: Object }
        };
    }
    constructor() {
        super();
        this.type = 'information';
        this.iconType = '';
        this.isClosable = false;
        this.isNotificationDisplayed = true;
        this._hasCustomIcon = false;
    }
    hideNotification(e) {
        e.preventDefault();
        e.stopPropagation();
        this.isNotificationDisplayed = false;
        let isNotificationClosedEvent = new CustomEvent('rui-inline-notification-close', {
            detail: !this.isNotificationDisplayed,
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(isNotificationClosedEvent);
    }
    render() {
        let statusIconType = this.type.toLowerCase();
        let notificationClassName = 'rui-inline-notification-info';
        let iconContext = '';
        if (statusIconType === 'error') {
            notificationClassName = 'rui-inline-notification-error';
            iconContext = 'status-icon-error';
        }
        else if (statusIconType === 'warning') {
            notificationClassName = 'rui-inline-notification-warning';
            iconContext = 'status-icon-warning';
        }
        else if (statusIconType === 'success') {
            notificationClassName = 'rui-inline-notification-success';
            iconContext = 'status-icon-success';
        }
        else {
            notificationClassName = 'rui-inline-notification-info';
            iconContext = 'status-icon-information';
        }
        if (this.querySelector(`*[slot='custom-icon']`)) {
            this._hasCustomIcon = true;
        }
        const classes = {
            ['rui-inline-notification']: true,
            [notificationClassName]: true,
            ['rui-inline-notification-hidden']: !this.isNotificationDisplayed
        };
        return html `
            <div class="${classMap(classes)}">
                <div class="rui-inline-notification-container">
                    ${this.iconType || this._hasCustomIcon ? html `<rui-icon iconType="${this.iconType}" context="${iconContext}" size="18px" class="rui-inline-notification-icon" aria-hidden="true"><slot name="custom-icon"></slot></rui-icon>` : ''}
                    <div class="rui-inline-notification-content">
                        <slot></slot>
                    </div>
                    ${this.isClosable ? html `<button type="button" class="close" aria-label="Close" @click="${this.hideNotification}"><span aria-hidden="true">&times;</span></button>` : ''}
                </div>
            </div>
        `;
    }
    static get styles() {
        return css `
            :host {
                display: block;
            }

            .rui-inline-notification {
                border-radius: 10px;
                margin: 0 0 15px 0;
                padding: 15px;
            }
            
            .rui-inline-notification-info {
                background-color: var(--rui-notification-info-bg, #ebf2ff);
                color: var(--rui-notification-info-fg, #666);
            }
            
            .rui-inline-notification-error {
                background-color: var(--rui-notification-error-bg, #fde1df);
                color: var(--rui-notification-error-fg, #666);
            }
            
            .rui-inline-notification-warning {
                background-color: var(--rui-notification-warning-bg, #f8edd8);
                color: var(--rui-notification-warning-fg, #666);
            }
            
            .rui-inline-notification-success {
                background-color: var(--rui-notification-success-bg, #e0f1e0);
                color: var(--rui-notification-success-fg, #666);
            }
            
            .rui-inline-notification-container {
                display: flex;
                align-items: flex-start;
            }
            
            .rui-inline-notification-icon {
                flex: 0 0 auto;
                margin: 1.5px 10px -4px 0px;
            }
            
            .rui-inline-notification-content {
                flex: 1 0 0%;
            }
            
            button.close {
                background: none;
                border: none;
                box-sizing: border-box;
                cursor: pointer;
                font-family: var(--rui-font-family, "Noto Sans", Roboto, Arial, -apple-system, system-ui, sans-serif);
                font-size: 21px;
                font-weight: 700;
                line-height: 1;
                margin: 0 0 0 5px;
                max-height: 20px;
                padding: 0 3px;
                opacity: 1;
                text-shadow: none;
                transition: color 0.25s;
            }
            
            @media (prefers-reduced-motion: reduce) {
                button.close {
                    transition: none;
                }
            }

            .rui-inline-notification-success button.close:hover,
            .rui-inline-notification-info button.close:hover,
            .rui-inline-notification-warning button.close:hover,
            .rui-inline-notification-error button.close:hover {
                color: var(--rui-text-regular, #09175b);
            }
            
            .rui-inline-notification-success button.close:focus,
            .rui-inline-notification-success button.close:active,
            .rui-inline-notification-info button.close:focus,
            .rui-inline-notification-info button.close:active,
            .rui-inline-notification-warning button.close:focus,
            .rui-inline-notification-warning button.close:active,
            .rui-inline-notification-error button.close:focus,
            .rui-inline-notification-error button.close:active {
                color: var(--rui-text-regular, #09175b);
                outline: var(--rui-outline-width, 1px) var(--rui-outline-style, solid) var(--rui-btn-primary-border, #085ce5);
                outline-offset: var(--rui-outline-offset-btn, 0);
            }
            
            .rui-inline-notification-success button.close {
                color: var(--rui-notification-success-close-btn, rgba(102, 102, 102, 0.5));
            }
            
            .rui-inline-notification-info button.close {
                color: var(--rui-notification-info-close-btn, rgba(102, 102, 102, 0.5));
            }
            
            .rui-inline-notification-warning button.close {
                color: var(--rui-notification-warning-close-btn, rgba(102, 102, 102, 0.5));
            }
            
            .rui-inline-notification-error button.close {
                color: var(--rui-notification-error-close-btn, rgba(102, 102, 102, 0.5));
            }
            
            .rui-inline-notification-hidden {
                display: none;
            }

            @media print {
                .rui-inline-notification {
                    background-color: transparent;
                    border-color: #000000;
                    color: #000000;
                }
            }
        `;
    }
}
customElements.get(RuiInlineNotification.is) || customElements.define(RuiInlineNotification.is, RuiInlineNotification);
